import { useQuery, useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { config } from "../../../environment";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { formatTimestamp } from "../../../lib/formatTimestamp";
import {
  useMakeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { TabHeaderBar, Tabs } from "../../TabHeader";
import { TabLink } from "../../TabLink";
import { Text } from "../../Text";
import {
  Container,
  HeaderActions,
  HoverActions,
  NeutralIconBadge,
  PillBadge,
  RowNameContent,
  RowNameLayout,
  RowNameLine,
  ScrollArea,
} from "../shared";
import { CreateNetworkModal } from "./CreateNetworkModal";
import { type EditingRule, RuleModal } from "./RuleModal";

const KIND_LABELS: Record<string, string> = {
  cloudflareAccessHttp: "Cloudflare Access (HTTP)",
  tunnel: "Tunnel",
};

function typeLabel(type: "hostnameGlob" | "cidr"): string {
  return type === "hostnameGlob" ? "Hostname pattern" : "CIDR";
}

export function NetworksSettings() {
  const makeUrl = useMakeUrl();
  const [networks, networksResult] = useQuery(queries.networks());
  const [rules, rulesResult] = useQuery(queries.egressRules());
  const [onRules] = useRouteMatch(routes.settings.networking.rules);
  const [onNewNetwork] = useRouteMatch(routes.settings.networking.new);
  const [onNewRule] = useRouteMatch(routes.settings.networking.rules.new);
  const { ruleId } = useCurrentUrl(routes.settings.networking.rules);
  const canManageNetworks = useHasAccess("tenant:networks_manage");
  const canCreateNetworks =
    useHasAccess("tenant:network_create") || canManageNetworks;
  const canManageRules = useHasAccess("tenant:egress_rules_manage");
  const z = useZero();
  const [confirmDelete, setConfirmDelete] = useState<{
    kind: "rule" | "network";
    id: string;
    name: string;
  } | null>(null);

  const editingRule = useMemo<EditingRule | undefined>(() => {
    if (!ruleId) return undefined;
    const rule = rules.find((r) => r.id === ruleId);
    return rule
      ? {
          id: rule.id,
          destinationType: rule.destinationType,
          destination: rule.destination,
          port: rule.port,
          action: rule.action,
        }
      : undefined;
  }, [ruleId, rules]);

  const networkItems = [...networks]
    .sort((a, b) => {
      const aDisabled = a.disabledAt != null ? 1 : 0;
      const bDisabled = b.disabledAt != null ? 1 : 0;
      if (aDisabled !== bDisabled) return aDisabled - bDisabled;
      return (b.createdAt ?? 0) - (a.createdAt ?? 0);
    })
    .map((network) => ({
      key: network.id,
      link: makeUrl(routes.settings.networking, {
        modal: "network",
        "modal[id]": network.id,
        "modal[tab]": "settings",
      }),
      name: (
        <RowNameLayout>
          <NeutralIconBadge iconName="earth" />
          <RowNameContent>
            <RowNameLine>
              <Text variant="text-regular-md" ellipsis>
                {network.name}
              </Text>
              {network.disabledAt != null && <PillBadge>Disabled</PillBadge>}
            </RowNameLine>
            <Text variant="text-small" color="var(--text-secondary)" ellipsis>
              {KIND_LABELS[network.identifier] ?? network.identifier}
            </Text>
          </RowNameContent>
        </RowNameLayout>
      ),
      created: (
        <Text variant="text-small" color="var(--text-secondary)">
          {formatTimestamp(network.createdAt)}
        </Text>
      ),
      actions: (hasMouse: boolean) =>
        canManageNetworks ? (
          <HoverActions visible={hasMouse}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="bottom"
              minWidth={160}
              sections={[
                {
                  items: [
                    {
                      type: "button" as const,
                      left: <Icon name="trash" size={16} />,
                      label: "Delete",
                      onClick: () =>
                        setConfirmDelete({
                          kind: "network",
                          id: network.id,
                          name: network.name,
                        }),
                    },
                  ],
                },
              ]}
            />
          </HoverActions>
        ) : null,
    }));

  const ruleItems = [...rules].map((rule) => ({
    key: rule.id,
    link: canManageRules
      ? makeUrl(routes.settings.networking.rules, { ruleId: rule.id })
      : undefined,
    action: (
      <PillBadge
        style={
          rule.action === "allow"
            ? { background: "var(--green-200)", color: "var(--green-800)" }
            : { background: "var(--red-200)", color: "var(--red-800)" }
        }
      >
        {rule.action === "allow" ? "Allow" : "Deny"}
      </PillBadge>
    ),
    name: (
      <RowNameLayout>
        <NeutralIconBadge iconName="shield-check" />
        <RowNameContent>
          <Text variant="text-regular-md" ellipsis>
            {rule.destination}
          </Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {typeLabel(rule.destinationType)}
          </Text>
        </RowNameContent>
      </RowNameLayout>
    ),
    port: (
      <Text variant="text-regular" color="var(--text-secondary)">
        {rule.port ?? "Any"}
      </Text>
    ),
    created: (
      <Text variant="text-small" color="var(--text-secondary)">
        {formatTimestamp(rule.createdAt)}
      </Text>
    ),
    actions: (hasMouse: boolean) =>
      canManageRules ? (
        <HoverActions visible={hasMouse}>
          <Dropdown
            opener={<Button variant="secondary" icon="more" />}
            align="bottom"
            minWidth={160}
            sections={[
              {
                items: [
                  {
                    type: "button" as const,
                    left: <Icon name="trash" size={16} />,
                    label: "Delete",
                    onClick: () =>
                      setConfirmDelete({
                        kind: "rule",
                        id: rule.id,
                        name: rule.destination,
                      }),
                  },
                ],
              },
            ]}
          />
        </HoverActions>
      ) : null,
  }));

  const networksComplete = networksResult.type === "complete";
  const rulesComplete = rulesResult.type === "complete";

  const createItems = [
    ...(config.tunnelEnabled && canCreateNetworks
      ? [
          {
            type: "button" as const,
            left: <Icon name="earth" size={16} />,
            label: "Network",
            onClick: () => navigate(routes.settings.networking.new),
          },
        ]
      : []),
    ...(canManageRules
      ? [
          {
            type: "button" as const,
            left: <Icon name="shield-check" size={16} />,
            label: "Rule",
            onClick: () => navigate(routes.settings.networking.rules.new),
          },
        ]
      : []),
  ];

  return (
    <Container>
      <TabHeaderBar>
        <Tabs>
          <TabLink
            to={makeUrl(routes.settings.networking)}
            active={!onRules}
            icon="earth"
            label="Networks"
          />
          <TabLink
            to={makeUrl(routes.settings.networking.rules)}
            active={onRules}
            icon="shield-check"
            label="Rules"
          />
        </Tabs>
        <HeaderActions>
          {createItems.length > 0 && (
            <Dropdown
              opener={<Button size="l" icon="plus" />}
              align="bottom"
              minWidth={180}
              sections={[{ label: "Create new", items: createItems }]}
            />
          )}
        </HeaderActions>
      </TabHeaderBar>

      {onRules ? (
        <ScrollArea>
          <ListTable
            headers={{
              name: "Destination",
              action: "Action",
              port: "Port",
              created: "Created",
              actions: "",
            }}
            items={ruleItems}
            emptyState={
              rulesComplete ? (
                <EmptyState
                  iconName="shield-check"
                  title="No rules"
                  description="Allow or deny the destinations a step can reach."
                />
              ) : null
            }
          />
        </ScrollArea>
      ) : (
        <ScrollArea>
          <ListTable
            headers={{ name: "Name", created: "Created", actions: "" }}
            items={networkItems}
            emptyState={
              networksComplete ? (
                <EmptyState
                  iconName="earth"
                  title="No networks"
                  description="Reach a private network through a secure tunnel."
                />
              ) : null
            }
          />
        </ScrollArea>
      )}

      {onNewNetwork && (
        <CreateNetworkModal
          onClose={() => navigate(routes.settings.networking)}
        />
      )}

      {(onNewRule || editingRule) && (
        <RuleModal
          editing={editingRule}
          onClose={() =>
            navigate(routes.settings.networking.rules, { ruleId: "" })
          }
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            if (confirmDelete.kind === "rule") {
              z.mutate(
                sharedMutators.deleteEgressRule({ id: confirmDelete.id })
              );
            } else {
              z.mutate(
                sharedMutators.deleteNetwork({ networkId: confirmDelete.id })
              );
            }
            setConfirmDelete(null);
          }}
          title={
            confirmDelete.kind === "rule" ? "Delete rule" : "Delete network"
          }
          description={
            confirmDelete.kind === "rule"
              ? `The rule for “${confirmDelete.name}” will be removed.`
              : `“${confirmDelete.name}” will be removed. Steps that route through this network will stop reaching their destinations.`
          }
          confirmLabel="Delete"
          destructive
        />
      )}
    </Container>
  );
}
